import * as S from './ExpressionEditor.styles';

import * as _ from 'lodash';
import * as React from 'react';
import {useEffect} from 'react';
import makeComp from '../../util/profiler';
import * as CG from './cg/graph';
import * as HL from './cg/hl';
import * as Code from './cg/code';
import * as EEState from './expEditorState';
import {ComputeGraphViz} from './ComputeGraphViz';

import InlineStringEditor from './editors/InlineStringEditor';
import InlineNumberEditor from './editors/InlineNumberEditor';

const ArgsEditor2: React.FC<{
  args: HL.EditingOpInputs;
}> = makeComp(
  ({args}) => {
    const argNames = Object.keys(args);
    const argValues = Object.values(args);
    return (
      <>
        (
        {argValues.map((inNode, i) => (
          <span key={i}>
            {argNames[i]}: {<NodeEditor node={inNode} />}
            {i < argNames.length - 1 && (
              <span style={{pointerEvents: 'none'}}>,&nbsp;</span>
            )}
          </span>
        ))}
        )
      </>
    );
  },
  {id: 'ArgEditor2'}
);

const OpEditor: React.FC<{op: HL.EditingOp}> = makeComp(
  ({op}) => {
    const argNames = Object.keys(op.inputs);
    const argValues = Object.values(op.inputs);
    if (HL.isBinaryOp(op)) {
      // Binary op
      return (
        <span>
          <NodeEditor node={argValues[0]} /> {HL.binaryOpSymbol(op)}{' '}
          <NodeEditor node={argValues[1]} />
        </span>
      );
    }
    if (HL.isChainedUnaryOp(op)) {
      // Render "pick" as index notation
      if (op.name === 'pick') {
        return (
          <span>
            <NodeEditor node={argValues[0]} />
            [
            <NodeEditor node={argValues[1]} />]
          </span>
        );
      }
      return (
        <span>
          <NodeEditor node={argValues[0]} />.{HL.opDisplayName(op)}
          <ArgsEditor2
            args={_.pickBy(op.inputs, (v, k) => k !== argNames[0])}
          />
        </span>
      );
    }
    // Render as function call
    return (
      <span>
        {HL.opDisplayName(op)}
        <ArgsEditor2 args={op.inputs} />
      </span>
    );
  },
  {id: 'OpEditor'}
);

const OutputNodeEditor: React.FC<{node: HL.EditingOutputNode}> = makeComp(
  ({node}) => {
    const focusedNode = EEState.useFocusedNode();
    return (
      <>
        <OpEditor op={node.fromOp} />
        {focusedNode === node && <AutoSuggestor node={node} />}
      </>
    );
  },
  {id: 'OutputNodeEditor'}
);

const VarNodeEditor: React.FC<{
  node: CG.VarNode;
}> = makeComp(
  ({node}) => {
    const focusedNode = EEState.useFocusedNode();
    return (
      <>
        {node.varName}
        {focusedNode === node && <AutoSuggestor node={node} />}
      </>
    );
  },
  {id: 'VarNodeEditor'}
);

const VoidNodeEditor: React.FC<{
  node: CG.VoidNode;
}> = makeComp(
  ({node}) => {
    const focusedNode = EEState.useFocusedNode();
    return focusedNode === node ? (
      <AutoSuggestor node={node} />
    ) : (
      <span>&nbsp;</span>
    );
  },
  {id: 'VoidNodeEditor'}
);

const ConstStringNodeEditor: React.FC<{
  node: CG.ConstNode<'string'>;
}> = makeComp(
  ({node}) => {
    return (
      <>
        "
        <AutoSuggestor node={node} defaultValue={node.val} />"
      </>
    );
  },
  {id: 'ConstStringNodeEditor'}
);

const ConstNumberNodeEditor: React.FC<{
  node: CG.ConstNode<'number'>;
  extraSuggestor?: JSX.Element;
}> = makeComp(
  ({node, extraSuggestor}) => {
    const focusNode = EEState.useAction(EEState.focusNode);
    // const blur = EEState.useAction(EEState.blur);
    const setBuffer = EEState.useAction(EEState.setBuffer);
    const updateNodeAndFocus = EEState.useAction(EEState.updateNodeAndFocus);
    const focusedNode = EEState.useFocusedNode();
    const cursorPos = EEState.useCursorPos();
    return (
      <InlineNumberEditor
        value={node.val}
        onFocus={() => focusNode(node)}
        // onBlur={blur}
        autofocus={focusedNode === node}
        defaultCursorPos={focusedNode === node ? cursorPos : undefined}
        // onFocus={() => focusOnNode(node)}
        extraSuggestor={extraSuggestor}
        // TODO: Min and max are unused in InlineNumberEditor, remove them?
        min={0}
        max={0}
        setValue={val => {
          updateNodeAndFocus({...node, val});
        }}
        onBufferChange={setBuffer}
      />
    );
  },
  {id: 'ConstNumberNodeEditor'}
);

const AutoSuggestor: React.FC<{
  node: HL.EditingNode;
  defaultValue?: string;
}> = makeComp(
  ({node, defaultValue}) => {
    const focusNode = EEState.useAction(EEState.focusNode);
    // const blur = EEState.useAction(EEState.blur);
    const setBuffer = EEState.useAction(EEState.setBuffer);
    const deletePrev = EEState.useAction(EEState.deletePrev);
    const suggestions = EEState.useSuggestions();
    const updateNodeAndFocus = EEState.useAction(EEState.updateNodeAndFocus);
    const focusedNode = EEState.useFocusedNode();
    return (
      <InlineStringEditor
        noQuotes
        // key={tailOpKey.toString()}
        onFocus={() => focusNode(node)}
        // onBlur={blur}
        autocompleteOptions={suggestions.map((sugg, i) => ({
          name: sugg.suggestionString,
          value: i,
        }))}
        autofocus={focusedNode === node}
        value={defaultValue ?? ''}
        onKeyDown={e => {
          // shift-enter
          if (e.keyCode === 13 && e.shiftKey) {
            // if (onAccept != null) {
            //   onAccept();
            // }
          }
          // backspace
          if (e.keyCode === 8 && window.getSelection()?.anchorOffset === 0) {
            deletePrev();
          }
        }}
        setValue={val => {
          if (_.isNumber(val)) {
            const pickedSuggestion = suggestions[val as any];
            updateNodeAndFocus(pickedSuggestion.newNode);
          } else {
            // TODO:
          }
        }}
        onBufferChange={setBuffer}
      />
    );
  },
  {id: 'AutoSuggestor'}
);

const ConstNodeEditor: React.FC<{
  node: CG.ConstNode;
}> = makeComp(
  // TODO: What to do if inputType is no longer valid for a step?
  ({node}) => {
    return CG.constNodeIsType(node, 'string') ? (
      <ConstStringNodeEditor node={node} />
    ) : CG.constNodeIsType(node, 'number') ? (
      <ConstNumberNodeEditor node={node} />
    ) : (
      <div>No editor for type: {JSON.stringify(node.type, undefined, 2)}</div>
    );
  },
  {id: 'ConstNodeEditor'}
);

// Note: using this will break DAGs. We'll need a normalized
// representation if we want to avoid that.
export const NodeEditor: React.FC<{
  node: HL.EditingNode;
}> = makeComp(
  // TODO: What to do if inputType is no longer valid for a step?
  ({node}) => {
    return (
      <S.ExpressionEditor>
        {node.nodeType === 'var' ? (
          <VarNodeEditor node={node} />
        ) : node.nodeType === 'output' ? (
          <OutputNodeEditor node={node} />
        ) : node.nodeType === 'const' ? (
          <ConstNodeEditor node={node} />
        ) : (
          <VoidNodeEditor node={node} />
        )}
      </S.ExpressionEditor>
    );
  },
  {id: 'NodeEditor'}
);

export const ExpressionDebug: React.FC<{}> = makeComp(
  // TODO: What to do if inputType is no longer valid for a step?
  () => {
    const node = EEState.useTailNode();
    const focus = EEState.useFocusedNode();
    return (
      <ComputeGraphViz
        node={node}
        highlightNode={focus}
        width={600}
        height={300}
      />
    );
  },
  {id: 'ExpressionDebug'}
);

export const ExpressionEditorContainer: React.FC<{
  focusOnMount?: boolean;
  onAccept?: () => void;
  debug?: boolean;
}> = makeComp(
  // TODO: What to do if inputType is no longer valid for a step?
  ({focusOnMount, onAccept, debug}) => {
    const node = EEState.useTailNode();
    const focusOnTail = EEState.useAction(EEState.focusOnTail);
    useEffect(() => {
      if (focusOnMount) {
        // Use a set timeout. If we're rendering inside a semantic popup,
        // we need to delay a little bit to ensure we're in our final position
        setTimeout(() => {
          focusOnTail();
        }, 1);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
      <S.ExpressionEditorContainer
        data-test="expression-editor-container"
        onClick={e => {
          if (e.currentTarget === e.target) {
            focusOnTail();
          }
        }}>
        <NodeEditor node={node as any} />
        <div style={{width: 20, display: 'inline-block'}} />
        {debug && <ExpressionDebug />}
      </S.ExpressionEditorContainer>
    );
  },
  {id: 'ExpressionEditorContainer'}
);

export const ExpressionEditor: React.FC<{
  focusOnMount?: boolean;
  frame?: Code.Frame;
  node: HL.EditingNode;
  onAccept?: () => void;
  debug?: boolean;
  updateNode(newNode: HL.EditingNode): void;
}> = makeComp(
  // TODO: What to do if inputType is no longer valid for a step?
  ({focusOnMount, frame, node, updateNode, onAccept, debug}) => {
    return (
      <EEState.EEContextProvider
        node={node}
        frame={frame}
        updateNode={updateNode}>
        <ExpressionEditorContainer
          focusOnMount={focusOnMount}
          onAccept={onAccept}
          debug={debug}
        />
      </EEState.EEContextProvider>
    );
  },
  {id: 'ExpressionEditor'}
);

export const ExpressionEditorWrapper = S.ExpressionEditorWrapper;
